import { HttpClient, HttpStatusCode } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, shareReplay } from 'rxjs/operators';

type EC2DataMapType = {
  connections: Record<string, Observable<EC2ConnectionType[]>>;
  instanceTypes?: Observable<string[]>;
  regions: Record<string, Observable<EC2RegionType[]>>;
  buckets: Record<string, Observable<EC2BucketsType[]>>;
  securityGroups: Record<string, Observable<EC2SecurityGroupType[]>>;
  subnets: Record<string, Observable<EC2SubnetType[]>>;
  aims: Record<string, Observable<EC2AimType[]>>;
};
export type EC2ConnectionType = { name: string; id: string; bucket: string; connectionId: string };
export type EC2RegionType = { name: string; value: string; region: string };
export type EC2BucketsType = { name: string; value: string };
export type EC2AimType = { name: string; value: string };
export type EC2SubnetType = {
  name: string;
  subnetId: string;
  availabilityZone: string;
  vpcId: string;
};
export type EC2SecurityGroupType = { name: string; value: string };
export type TryAddDestinationParams = { bucketName: string; UserId: string; accountId: string; region: string };
export type TryAddDestinationResponse = { StatusCode: HttpStatusCode; PayLoad: string };

type AzureDataMapType = {
  bootDiagnosticStorages: Record<string, Observable<AzureBootDiagnosticStorageType[]>>;
  connections: Record<string, Observable<AzureConnectionType[]>>;
  containers: Record<string, Observable<AzureContainerType[]>>;
  locations: Record<string, Observable<AzureLocationType[]>>;
  networks: Record<string, Observable<AzureNetworkType[]>>;
  resourceGroups: Record<string, Observable<string[]>>;
  storages: Record<string, Observable<AzureStorageType[]>>;
  subnets: Record<string, Observable<AzureSubnetType[]>>;
  vmSizes: Record<string, Observable<AzureVMSizeType[]>>;
};

export type AzureConnectionType = { name?: string; id?: string; bucket?: string; connectionId?: string };
export type AzureLocationType = { name?: string; value?: string; region?: string };
export type AzureVMSizeType = { name?: string; coresNum?: string; memoryInMb?: string };
export type AzureNetworkType = { name?: string; value?: string };
export type AzureSubnetType = { name?: string; value?: string };
export type AzureStorageType = { name?: string; value?: string };
export type AzureContainerType = { name?: string; value?: string };
export type AzureBootDiagnosticStorageType = { name?: string; value?: string };

@Injectable({ providedIn: 'root' })
export class RestoreToCloudService {
  private http = inject(HttpClient);

  private readonly ec2BaseUrl = '/api/restore2cloud/ec2';
  private readonly azureBaseUrl = '/api/restore2cloud/azure';

  public ec2DataMap: EC2DataMapType = {
    aims: {},
    connections: {},
    regions: {},
    buckets: {},
    instanceTypes: null,
    securityGroups: {},
    subnets: {}
  };
  public azureDataMap: AzureDataMapType = {
    bootDiagnosticStorages: {},
    connections: {},
    containers: {},
    locations: {},
    networks: {},
    resourceGroups: {},
    storages: {},
    subnets: {},
    vmSizes: {}
  };

  // EC2 Section
  public getEC2Connections(userId: string): Observable<EC2ConnectionType[]> {
    const cachedConnections$ = this.ec2DataMap.connections[userId];
    if (cachedConnections$) return cachedConnections$;

    this.ec2DataMap.connections[userId] = this.http.get<EC2ConnectionType[]>(this.ec2BaseUrl + '/connections', { params: { userId } }).pipe(
      catchError((error) => {
        delete this.ec2DataMap.connections[userId];
        return throwError(() => error);
      }),
      shareReplay({ bufferSize: 1, refCount: false })
    );

    return this.ec2DataMap.connections[userId];
  }

  public getEC2Regions(connectionId: string): Observable<EC2RegionType[]> {
    const cachedRegions$ = this.ec2DataMap.regions[connectionId];
    if (cachedRegions$) return cachedRegions$;

    this.ec2DataMap.regions[connectionId] = this.http.get<EC2RegionType[]>(this.ec2BaseUrl + '/regions', { params: { connectionId } }).pipe(
      catchError((error) => {
        delete this.ec2DataMap.regions[connectionId];
        return throwError(() => error);
      }),
      shareReplay({ bufferSize: 1, refCount: false })
    );

    return this.ec2DataMap.regions[connectionId];
  }

  public getEC2InstanceTypes(): Observable<string[]> {
    const cachedInstanceTypes$ = this.ec2DataMap.instanceTypes;
    if (cachedInstanceTypes$) return cachedInstanceTypes$;

    this.ec2DataMap.instanceTypes = this.http.get<string[]>(this.ec2BaseUrl + '/instance-types').pipe(
      catchError((error) => {
        delete this.ec2DataMap.instanceTypes;
        return throwError(() => error);
      }),
      shareReplay({ bufferSize: 1, refCount: false })
    );

    return this.ec2DataMap.instanceTypes;
  }

  public getEC2Buckets(userId: string, connectionId: string, region: string): Observable<EC2BucketsType[]> {
    const cacheKey = this.getEC2BucketsCacheKey(userId, connectionId, region);
    const cachedBuckets$ = this.ec2DataMap.buckets[cacheKey];
    if (cachedBuckets$) return cachedBuckets$;

    this.ec2DataMap.buckets[cacheKey] = this.http
      .get<EC2BucketsType[]>(this.ec2BaseUrl + '/buckets', { params: { userId, connectionId, region } })
      .pipe(
        catchError((error) => {
          delete this.ec2DataMap.buckets[cacheKey];
          return throwError(() => error);
        }),
        shareReplay({ bufferSize: 1, refCount: false })
      );

    return this.ec2DataMap.buckets[cacheKey];
  }

  public invalidateEC2Buckets(userId: string, connectionId: string, region: string): void {
    delete this.ec2DataMap.buckets[this.getEC2BucketsCacheKey(userId, connectionId, region)];
  }

  public getEC2Subnets(connectionId: string, region: string): Observable<EC2SubnetType[]> {
    const cacheKey = `${connectionId}-${region}`;
    const cachedSubnets$ = this.ec2DataMap.subnets[cacheKey];
    if (cachedSubnets$) return cachedSubnets$;

    this.ec2DataMap.subnets[cacheKey] = this.http
      .get<EC2SubnetType[]>(this.ec2BaseUrl + '/subnets', { params: { connectionId, region } })
      .pipe(
        catchError((error) => {
          delete this.ec2DataMap.subnets[cacheKey];
          return throwError(() => error);
        }),
        shareReplay({ bufferSize: 1, refCount: false })
      );

    return this.ec2DataMap.subnets[cacheKey];
  }

  public getEC2Aims(connectionId: string, region: string): Observable<EC2AimType[]> {
    const cacheKey = `${connectionId}-${region}`;
    const cachedAims$ = this.ec2DataMap.aims[cacheKey];
    if (cachedAims$) return cachedAims$;

    this.ec2DataMap.aims[cacheKey] = this.http.get<EC2AimType[]>(this.ec2BaseUrl + '/aims', { params: { connectionId, region } }).pipe(
      catchError((error) => {
        delete this.ec2DataMap.aims[cacheKey];
        return throwError(() => error);
      }),
      shareReplay({ bufferSize: 1, refCount: false })
    );

    return this.ec2DataMap.aims[cacheKey];
  }

  public getEC2SecurityGroups(connectionId: string, region: string, subnet: string): Observable<EC2SecurityGroupType[]> {
    const cacheKey = `${connectionId}-${region}-${subnet}`;
    const cachedSecurityGroups$ = this.ec2DataMap.securityGroups[cacheKey];
    if (cachedSecurityGroups$) return cachedSecurityGroups$;

    this.ec2DataMap.securityGroups[cacheKey] = this.http
      .get<EC2SecurityGroupType[]>(this.ec2BaseUrl + '/security-groups', { params: { connectionId, region, subnet } })
      .pipe(
        catchError((error) => {
          delete this.ec2DataMap.securityGroups[cacheKey];
          return throwError(() => error);
        }),
        shareReplay({ bufferSize: 1, refCount: false })
      );

    return this.ec2DataMap.securityGroups[cacheKey];
  }

  public tryAddDestination(params: TryAddDestinationParams): Observable<TryAddDestinationResponse> {
    return this.http.post<TryAddDestinationResponse>('/Admin/CloudPlans/TryAddDestination', params);
  }

  private getEC2BucketsCacheKey(userId: string, connectionId: string, region: string): string {
    return `${userId}-${connectionId}-${region}`;
  }

  // Azure Section
  public getAzureConnections(userId: string): Observable<AzureConnectionType[]> {
    const cachedConnections$ = this.azureDataMap.connections[userId];
    if (cachedConnections$) return cachedConnections$;

    this.azureDataMap.connections[userId] = this.http
      .get<AzureConnectionType[]>(this.azureBaseUrl + '/connections', { params: { userId } })
      .pipe(
        catchError((error) => {
          delete this.azureDataMap.connections[userId];
          return throwError(() => error);
        }),
        shareReplay({ bufferSize: 1, refCount: false })
      );

    return this.azureDataMap.connections[userId];
  }

  public getAzureLocations(connectionId: string): Observable<AzureLocationType[]> {
    const cachedLocations$ = this.azureDataMap.locations[connectionId];
    if (cachedLocations$) return cachedLocations$;

    this.azureDataMap.locations[connectionId] = this.http
      .get<AzureLocationType[]>(this.azureBaseUrl + '/locations', { params: { connectionId } })
      .pipe(
        catchError((error) => {
          delete this.azureDataMap.locations[connectionId];
          return throwError(() => error);
        }),
        shareReplay({ bufferSize: 1, refCount: false })
      );

    return this.azureDataMap.locations[connectionId];
  }

  public getAzureResourceGroups(connectionId: string): Observable<string[]> {
    const cachedResourceGroups$ = this.azureDataMap.resourceGroups[connectionId];
    if (cachedResourceGroups$) return cachedResourceGroups$;

    this.azureDataMap.resourceGroups[connectionId] = this.http
      .get<string[]>(this.azureBaseUrl + '/resource-groups', { params: { connectionId } })
      .pipe(
        catchError((error) => {
          delete this.azureDataMap.resourceGroups[connectionId];
          return throwError(() => error);
        }),
        shareReplay({ bufferSize: 1, refCount: false })
      );

    return this.azureDataMap.resourceGroups[connectionId];
  }

  public getAzureVMSizes(connectionId: string, location: string): Observable<AzureVMSizeType[]> {
    const cacheKey = `${connectionId}-${location}`;
    const cachedVMSizes$ = this.azureDataMap.vmSizes[cacheKey];
    if (cachedVMSizes$) return cachedVMSizes$;

    this.azureDataMap.vmSizes[cacheKey] = this.http
      .get<AzureVMSizeType[]>(this.azureBaseUrl + '/vm-sizes', { params: { connectionId, location } })
      .pipe(
        catchError((error) => {
          delete this.azureDataMap.vmSizes[cacheKey];
          return throwError(() => error);
        }),
        shareReplay({ bufferSize: 1, refCount: false })
      );

    return this.azureDataMap.vmSizes[cacheKey];
  }

  public getAzureNetworks(connectionId: string, location: string, resourceGroup: string): Observable<AzureNetworkType[]> {
    const cacheKey = `${connectionId}-${location}-${resourceGroup}`;
    const cachedNetworks$ = this.azureDataMap.networks[cacheKey];
    if (cachedNetworks$) return cachedNetworks$;

    this.azureDataMap.networks[cacheKey] = this.http
      .get<AzureNetworkType[]>(this.azureBaseUrl + '/networks', { params: { connectionId, location, resourceGroup } })
      .pipe(
        catchError((error) => {
          delete this.azureDataMap.networks[cacheKey];
          return throwError(() => error);
        }),
        shareReplay({ bufferSize: 1, refCount: false })
      );

    return this.azureDataMap.networks[cacheKey];
  }

  public getAzureSubnets(connectionId: string, location: string, resourceGroup: string, network: string): Observable<AzureSubnetType[]> {
    const cacheKey = `${connectionId}-${location}-${resourceGroup}-${network}`;
    const cachedSubnets$ = this.azureDataMap.subnets[cacheKey];
    if (cachedSubnets$) return cachedSubnets$;

    this.azureDataMap.subnets[cacheKey] = this.http
      .get<AzureSubnetType[]>(this.azureBaseUrl + '/subnets', { params: { connectionId, location, resourceGroup, network } })
      .pipe(
        catchError((error) => {
          delete this.azureDataMap.subnets[cacheKey];
          return throwError(() => error);
        }),
        shareReplay({ bufferSize: 1, refCount: false })
      );

    return this.azureDataMap.subnets[cacheKey];
  }

  public getAzureStorages(connectionId: string, location: string, resourceGroup: string): Observable<AzureStorageType[]> {
    const cacheKey = `${connectionId}-${location}-${resourceGroup}`;
    const cachedStorages$ = this.azureDataMap.storages[cacheKey];
    if (cachedStorages$) return cachedStorages$;

    this.azureDataMap.storages[cacheKey] = this.http
      .get<AzureStorageType[]>(this.azureBaseUrl + '/storages', { params: { connectionId, location, resourceGroup } })
      .pipe(
        catchError((error) => {
          delete this.azureDataMap.storages[cacheKey];
          return throwError(() => error);
        }),
        shareReplay({ bufferSize: 1, refCount: false })
      );

    return this.azureDataMap.storages[cacheKey];
  }

  public getAzureContainers(connectionId: string, storage: string, resourceGroup: string): Observable<AzureContainerType[]> {
    const cacheKey = `${connectionId}-${storage}-${resourceGroup}`;
    const cachedContainers$ = this.azureDataMap.containers[cacheKey];
    if (cachedContainers$) return cachedContainers$;

    this.azureDataMap.containers[cacheKey] = this.http
      .get<AzureContainerType[]>(this.azureBaseUrl + '/containers', { params: { connectionId, storage, resourceGroup } })
      .pipe(
        catchError((error) => {
          delete this.azureDataMap.containers[cacheKey];
          return throwError(() => error);
        }),
        shareReplay({ bufferSize: 1, refCount: false })
      );

    return this.azureDataMap.containers[cacheKey];
  }

  public getAzureBootDiagnosticStorages(
    connectionId: string,
    location: string,
    resourceGroup: string
  ): Observable<AzureBootDiagnosticStorageType[]> {
    const cacheKey = `${connectionId}-${location}-${resourceGroup}`;
    const cachedBootDiagnosticStorages$ = this.azureDataMap.bootDiagnosticStorages[cacheKey];
    if (cachedBootDiagnosticStorages$) return cachedBootDiagnosticStorages$;

    this.azureDataMap.bootDiagnosticStorages[cacheKey] = this.http
      .get<AzureBootDiagnosticStorageType[]>(this.azureBaseUrl + '/boot-diagnostic-storages', {
        params: { connectionId, location, resourceGroup }
      })
      .pipe(
        catchError((error) => {
          delete this.azureDataMap.bootDiagnosticStorages[cacheKey];
          return throwError(() => error);
        }),
        shareReplay({ bufferSize: 1, refCount: false })
      );

    return this.azureDataMap.bootDiagnosticStorages[cacheKey];
  }
}
